import type { Col } from "../art/pal.ts";
import type { TypeId } from "./types.ts";

/**
 * Battle-only conditions. A creature carries at most one; switching out or leaving the battle
 * clears it (the BattleMon wrapper is rebuilt), so nothing here ever reaches a save.
 */
export const STATUS_IDS = ["scorch", "frost", "lag", "afk", "fud"] as const;
export type StatusId = (typeof STATUS_IDS)[number];

export interface StatusMeta {
  id: StatusId;
  name: string;
  /** Three-letter chip for plates and lists. */
  chip: string;
  col: Col;
  /** Creatures of this type shrug the condition off. */
  immune?: TypeId;
  /** One-line rule for the move card and tactics. */
  rule: string;
  /** "X is ___!" when it lands. */
  verb: string;
}

export const STATUS_META: Record<StatusId, StatusMeta> = {
  scorch: {
    id: "scorch", name: "Scorched", chip: "SCR", col: [255, 120, 60, 255], immune: "burn",
    rule: "Loses 1/12 HP each turn.", verb: "scorched",
  },
  frost: {
    id: "frost", name: "Frozen", chip: "FRZ", col: [130, 210, 255, 255], immune: "hodl",
    rule: "Frozen solid up to 2 turns; BURN hits thaw it.", verb: "frozen solid",
  },
  lag: {
    id: "lag", name: "Lagging", chip: "LAG", col: [250, 220, 90, 255], immune: "signal",
    rule: "Half speed; 1 in 4 turns lost to lag.", verb: "lagging",
  },
  afk: {
    id: "afk", name: "AFK", chip: "AFK", col: [170, 160, 255, 255],
    rule: "Skips 1-3 turns, then logs back in.", verb: "AFK",
  },
  fud: {
    id: "fud", name: "FUD'd", chip: "FUD", col: [200, 110, 230, 255], immune: "rug",
    rule: "Loses 1/16 HP, then 2/16, 3/16... each turn.", verb: "full of FUD",
  },
};

/** Catch bonus while the wild creature is afflicted: sleepers and ice blocks are easy to latch. */
export function statusCatchMul(s: StatusId | null | undefined): number {
  return !s ? 1 : s === "afk" || s === "frost" ? 1.5 : 1.25;
}
